import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import logo from '../../assets/logo.svg';
import menuMobileFrame from '../../assets/menu-mobile-frame.svg';
import './NavBar.css';
import {
  Link,
  useLocation,
} from 'react-router-dom';

import { ICONS, getIcon } from '../../icons';
import { getAppLoginUrl } from '../appUrl';
import { captureDemoModalOpened } from '../../analytics';

const navItems = [
  { key: '1', label: 'Home', link: '/' },
  { key: '2', label: 'Features', link: '/features' },
  { key: '7', label: 'Compare', link: '/compare' },
  { key: '3', label: 'Peg Board', link: '/pegboard' },
  { key: '4', label: 'Pricing', link: '/pricing' },
  { key: '5', label: 'Pulse', link: '/pulse/all' },
  { key: '6', label: 'FAQs', link: '/faq' },
];

const featuresItems = [
  { key: '21', label: 'For Club Managers', link: '/features/managers' },
  { key: '22', label: 'For Players', link: '/features/players' },
];

const isActivePath = (pathname, link) => (
  link === '/' ? pathname === '/' : pathname === link || pathname.startsWith(`${link}/`)
);

export default function NavBar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [desktopFeaturesOpen, setDesktopFeaturesOpen] = useState(false);
  const [mobileFeaturesOpen, setMobileFeaturesOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const closeButtonRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const featuresButtonRef = useRef(null);
  const location = useLocation();

  const appLoginUrl = getAppLoginUrl();
  const featuresActive = featuresItems.some((item) => isActivePath(location.pathname, item.link));

  useEffect(() => {
    setMenuOpen(false);
    setDesktopFeaturesOpen(false);
    setMobileFeaturesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleMenuKeyboard = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusableElements = Array.from(
        mobileMenuRef.current?.querySelectorAll('a[href], button:not([disabled])') ?? [],
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement?.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    };

    document.addEventListener('keydown', handleMenuKeyboard);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleMenuKeyboard);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!desktopFeaturesOpen) {
      return undefined;
    }

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setDesktopFeaturesOpen(false);
        featuresButtonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [desktopFeaturesOpen]);

  const modalStyles = {
    body: {
      height: '100%',
    },
    mask: {
      backdropFilter: 'blur(10px)',
    },
    content: {
      height: '100%',
      overflowY: 'scroll',
    },
  };

  const openDemoModal = (placement) => {
    captureDemoModalOpened(location.pathname, placement);
    setModalOpen(true);
  };

  const closeMobileMenu = () => {
    setMenuOpen(false);
    setMobileFeaturesOpen(false);
  };

  return (
    <header className="nav">
      <Modal
        title="Book a demo"
        centered
        open={modalOpen}
        footer={null}
        onCancel={() => setModalOpen(false)}
        width="min(1100px, calc(100% - 32px))"
        height="calc(100% - 32px)"
        styles={modalStyles}
        className="signup-modal"
      >
        <iframe
          title="Book a ShuttleOps demo"
          className="demo-frame"
          src="https://meet.brevo.com/andy-feltham/intro-to-shuttleops"
        />
      </Modal>

      <div className="nav-inner">
        <div className="nav-row">
          <Link className="nav-logo" to="/" aria-label="ShuttleOps home">
            <img src={logo} alt="ShuttleOps" />
          </Link>

          <nav className="nav-links" aria-label="Primary navigation">
            {navItems.map((item) => {
              if (item.label === 'Features') {
                return (
                  <div className="nav-feature" key={item.key}>
                    <button
                      ref={featuresButtonRef}
                      type="button"
                      className={`nav-link has-dropdown${featuresActive ? ' active' : ''}`}
                      aria-haspopup="true"
                      aria-expanded={desktopFeaturesOpen}
                      aria-controls="desktop-features-menu"
                      onClick={() => setDesktopFeaturesOpen((open) => !open)}
                    >
                      <span>{item.label}</span>
                      {getIcon(desktopFeaturesOpen ? ICONS.CARET_UP : ICONS.CARET_DOWN, true, {})}
                    </button>

                    {desktopFeaturesOpen && (
                      <div id="desktop-features-menu" className="nav-submenu">
                        {featuresItems.map((subItem) => (
                          <Link
                            key={subItem.key}
                            className="nav-sublink"
                            to={subItem.link}
                            aria-current={isActivePath(location.pathname, subItem.link) ? 'page' : undefined}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.key}
                  className={`nav-link${isActivePath(location.pathname, item.link) ? ' active' : ''}`}
                  to={item.link}
                  aria-current={isActivePath(location.pathname, item.link) ? 'page' : undefined}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="nav-ctas">
            <a className="nav-cta nav-secondary" href={appLoginUrl}>Login</a>
            <a className="nav-cta nav-primary" href={appLoginUrl} data-signup-cta="desktop_navigation">
              Sign Up Now
            </a>
            <button className="nav-cta nav-secondary" type="button" onClick={() => openDemoModal('desktop_navigation')}>
              Request a Demo
            </button>
          </div>

          <button
            ref={menuButtonRef}
            type="button"
            className="nav-menu-icon"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMenuOpen(true)}
          >
            {getIcon(ICONS.BARS, true, { size: 20, secondary: 'white', primary: 'white', secondaryOpacity: 1 })}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div
          ref={mobileMenuRef}
          className="mobile-menu-overlay"
          id="mobile-navigation"
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
        >
          <div className="nav-inner overlay-inner">
            <div className="nav-row">
              <Link className="nav-logo" to="/" aria-label="ShuttleOps home" onClick={closeMobileMenu}>
                <img src={logo} alt="ShuttleOps" />
              </Link>
              <button
                ref={closeButtonRef}
                type="button"
                className="nav-menu-icon close-icon"
                aria-label="Close menu"
                onClick={closeMobileMenu}
              >
                {getIcon(ICONS.XMARK, true, { size: 20, secondary: 'white', primary: 'white', secondaryOpacity: 1 })}
              </button>
            </div>
          </div>

          <nav className="mobile-menu-links" aria-label="Mobile navigation">
            {navItems.map((item) => {
              if (item.label === 'Features') {
                return (
                  <div className="mobile-feature" key={item.key}>
                    <button
                      type="button"
                      className={`mobile-link has-dropdown${featuresActive ? ' active' : ''}`}
                      aria-haspopup="true"
                      aria-expanded={mobileFeaturesOpen}
                      aria-controls="mobile-features-menu"
                      onClick={() => setMobileFeaturesOpen((open) => !open)}
                    >
                      <span>{item.label}</span>
                      {getIcon(mobileFeaturesOpen ? ICONS.CARET_UP : ICONS.CARET_DOWN, true, {})}
                    </button>

                    {mobileFeaturesOpen && (
                      <div id="mobile-features-menu" className="mobile-submenu">
                        {featuresItems.map((subItem) => (
                          <Link
                            key={subItem.key}
                            className="mobile-sublink"
                            to={subItem.link}
                            aria-current={isActivePath(location.pathname, subItem.link) ? 'page' : undefined}
                            onClick={closeMobileMenu}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.key}
                  className={`mobile-link${isActivePath(location.pathname, item.link) ? ' active' : ''}`}
                  to={item.link}
                  aria-current={isActivePath(location.pathname, item.link) ? 'page' : undefined}
                  onClick={closeMobileMenu}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="mobile-menu-ctas">
            <a className="mobile-cta primary" href={appLoginUrl} data-signup-cta="mobile_navigation">
              Sign Up Now
            </a>
            <button
              className="mobile-cta secondary"
              type="button"
              onClick={() => {
                closeMobileMenu();
                openDemoModal('mobile_navigation');
              }}
            >
              Request a Demo
            </button>
            <a className="mobile-cta secondary" href={appLoginUrl}>Login</a>
          </div>

          <img
            src={menuMobileFrame}
            alt=""
            aria-hidden="true"
            className="mobile-menu-frame"
          />
        </div>
      )}
    </header>
  );
}
